import { BellOutlined } from "@ant-design/icons"
import { type EventContentArg } from "@fullcalendar/core"
import { Flex, Typography } from "antd"
import dayjs from "dayjs"
import React from "react"

import {
  estimateEventHeightPx,
  pickEventCardTier
} from "../../helpers/calendar-event-card-layout.ts"
import { useElementSize } from "../../hooks/use-element-size.ts"

const { Text } = Typography

interface CalendarEventCellReminderProps {
  eventInfo: EventContentArg
}

export function CalendarEventCellReminder({
  eventInfo
}: CalendarEventCellReminderProps) {
  const event = eventInfo?.event
  const title = event?.title || ""
  const isCompleted = event?.extendedProps?.is_completed
  const isMonthView = eventInfo?.view?.type === "dayGridMonth"
  const isListView = !!eventInfo?.view?.type?.startsWith("list")

  if (isListView) {
    const lead = event?.extendedProps?.lead
    const project = event?.extendedProps?.project
    const recordName = project?.name || lead?.name

    return (
      <Flex vertical gap={2} className={isCompleted ? "line-through opacity-50" : ""}>
        <Flex align="center" gap={6}>
          <BellOutlined
            className="shrink-0"
            style={{ fontSize: "12px", color: `${eventInfo?.textColor}` }}
          />
          <Text strong className="text-sm">
            {title}
          </Text>
          {isCompleted && (
            <Text type="secondary" className="text-[11px] font-medium">
              Done
            </Text>
          )}
        </Flex>
        {recordName && (
          <Text type="secondary" className="text-xs">
            {recordName}
          </Text>
        )}
      </Flex>
    )
  }

  if (isMonthView) {
    return (
      <Flex
        align="center"
        gap={4}
        className={`size-full overflow-hidden ${isCompleted ? "line-through opacity-50" : ""}`}
      >
        <BellOutlined
          className="shrink-0"
          style={{ fontSize: "12px", color: `${eventInfo?.textColor}` }}
        />
        {eventInfo?.timeText && (
          <Text className="text-xs text-inherit opacity-80 leading-4 shrink-0">
            {eventInfo.timeText}
          </Text>
        )}
        <Text ellipsis className="text-xs text-inherit font-medium leading-4">
          {title}
        </Text>
      </Flex>
    )
  }

  return (
    <CalendarEventCellReminderBody
      eventInfo={eventInfo}
      isCompleted={isCompleted}
      title={title}
    />
  )
}

function CalendarEventCellReminderBody({
  eventInfo,
  isCompleted,
  title
}: {
  eventInfo: EventContentArg
  isCompleted: boolean
  title: string
}) {
  const event = eventInfo?.event
  const diffInMinutes = dayjs(event?.end).diff(dayjs(event?.start), "minute")

  // Same real-space-driven approach as the regular event card
  // (`CalendarEventCellEvent`) — the description used to render unconditionally
  // regardless of how little room a short reminder actually had for it.
  const { ref: sizeRef, size } = useElementSize<HTMLDivElement>({
    width: 200,
    height: estimateEventHeightPx(diffInMinutes)
  })
  const { showTime, hasExtraRoom } = pickEventCardTier(size)
  const description = event?.extendedProps?.description
  const showDescription = !!description && hasExtraRoom

  return (
    <div
      ref={sizeRef}
      className={`flex flex-col overflow-hidden size-full min-h-0 ${isCompleted ? "line-through opacity-50" : ""}`}
    >
      {eventInfo?.timeText && showTime && (
        <Text className="text-[10px] text-inherit opacity-75 leading-3 font-medium shrink-0">
          {eventInfo.timeText}
        </Text>
      )}
      <Flex align="center" gap={4} className="min-w-0 shrink-0">
        <BellOutlined
          className="shrink-0"
          style={{ fontSize: "12px", color: `${eventInfo?.textColor}` }}
        />
        <Text
          strong
          ellipsis
          className="text-[12px] text-inherit leading-4 min-w-0 flex-1"
        >
          {title}
        </Text>
      </Flex>
      {showDescription && (
        <Text
          className="text-[11px] text-inherit opacity-90 leading-3 shrink-0"
          ellipsis={{ tooltip: description }}
        >
          {description}
        </Text>
      )}
    </div>
  )
}
